// src/screens/admin/CotizadorUI.jsx
// Piezas compartidas del Cotizador BV: campos numéricos con coma decimal,
// colores de estado y el guardado automático con indicador.
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { diaNum, masDiasLlegada } from '../../services/cotizadorCalc';

export const inputStyle = { padding: '10px 12px', fontSize: 14, marginBottom: 0, width: '100%' };
export const cardStyle = {
  background: 'var(--navy-card)', borderWidth: 1, borderStyle: 'solid', borderColor: 'var(--navy-border)', borderRadius: 14, padding: 14, marginBottom: 12,
};
export const labelStyle = { display: 'block', fontSize: 10, fontWeight: 700, color: 'var(--muted)', letterSpacing: 0.5, marginBottom: 4, textTransform: 'uppercase' };
export const btnChico = {
  background: 'transparent', border: '1px solid var(--navy-border)', color: 'var(--muted)',
  borderRadius: 8, padding: '8px 12px', fontSize: 12, fontWeight: 600,
};

export const ESTADOS = {
  bien: { texto: 'Bien', corto: 'Bien', color: 'var(--success)' },
  poca: { texto: 'Poca ganancia', corto: 'Poca', color: 'var(--gold)' },
  pierdes: { texto: 'Pierdes', corto: 'Pierdes', color: 'var(--error)' },
  sin_omnibus: { texto: 'Ningún ómnibus llega a tiempo', corto: 'Sin bus', color: 'var(--error)' },
  dia_invalido: { texto: 'El vuelo sale antes de que llegue el cliente', corto: 'Día', color: 'var(--error)' },
};

const DIAS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
export const diaSemana = (iso) => {
  const n = diaNum(iso);
  return n === null ? '' : DIAS[(n + 4) % 7]; // el día 0 (1/1/1970) fue jueves
};

export function Campo({ label, children, style }) {
  return (
    <div style={style}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

// Campo numérico: en pantalla usa coma decimal, internamente guarda con punto.
// `escala` permite mostrar 0,1 como 10 (porcentajes).
export function NumInput({ value, onChange, placeholder, escala = 1, allowNegative = false }) {
  const mostrado = value === null || value === undefined || value === ''
    ? ''
    : escala === 1 ? String(value).replace('.', ',') : String(Math.round(Number(value) * escala * 1e6) / 1e6).replace('.', ',');
  return (
    <input
      type="text"
      inputMode="decimal"
      value={mostrado}
      placeholder={placeholder}
      style={inputStyle}
      onChange={(e) => {
        const crudo = e.target.value.replace(',', '.');
        const ok = allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/;
        if (!ok.test(crudo)) return;
        if (escala === 1 || crudo === '' || crudo === '-' || crudo.endsWith('.')) onChange(crudo);
        else onChange(String(Number(crudo) / escala));
      }}
    />
  );
}

// Guardado automático: junta los cambios por clave y los manda 0,7 s después
// del último. Si falla, los deja en cola y avisa. Al salir guarda lo pendiente.
export function useAutoGuardado() {
  const [estado, setEstado] = useState('guardado');
  const cola = useRef(new Map());
  const timer = useRef(null);

  const flush = useCallback(async () => {
    clearTimeout(timer.current);
    timer.current = null;
    const items = [...cola.current.entries()];
    if (!items.length) return;
    cola.current = new Map();
    setEstado('guardando');
    try {
      await Promise.all(items.map(([, i]) => i.fn(i.patch)));
      setEstado(cola.current.size ? 'pendiente' : 'guardado');
    } catch {
      items.forEach(([k, i]) => {
        const nuevo = cola.current.get(k);
        cola.current.set(k, { fn: i.fn, patch: { ...i.patch, ...(nuevo?.patch || {}) } });
      });
      setEstado('error');
    }
  }, []);

  const programar = useCallback((clave, patch, fn) => {
    const previo = cola.current.get(clave);
    cola.current.set(clave, { fn, patch: { ...(previo?.patch || {}), ...patch } });
    setEstado('pendiente');
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 700);
  }, [flush]);

  useEffect(() => () => { flush(); }, [flush]);

  return { estado, programar, flush };
}

export function IndicadorGuardado({ estado }) {
  const mapa = {
    guardado: ['✓ Guardado', 'var(--success)'],
    guardando: ['Guardando…', 'var(--muted)'],
    pendiente: ['Escribiendo…', 'var(--muted)'],
    error: ['⚠ No se guardó, reintenta', 'var(--error)'],
  };
  const [texto, color] = mapa[estado] || mapa.guardado;
  return <span style={{ fontSize: 12, fontWeight: 600, color }}>{texto}</span>;
}

// Lo que distingue a un vuelo de otro del mismo día: etiqueta, aerolínea, escalas y llegada.
export const textoEscalas = (e) => (e === null || e === undefined || e === '' ? '' : Number(e) === 0 ? 'directo' : `${Number(e)} ${Number(e) === 1 ? 'escala' : 'escalas'}`);
export function resumenFila(op) {
  return [
    op.tarjeta_ajustes?.etiqueta, op.sale_de === 'BVB' ? 'sale de Boa Vista' : '', op.ciudad, op.aerolinea, textoEscalas(op.escalas),
    op.hora_llegada_vuelo ? `llega ${op.hora_llegada_vuelo}${masDiasLlegada(op) > 0 ? ` (+${masDiasLlegada(op)})` : ''}` : '', op.nota,
  ].filter(Boolean).join(' · ');
}
